import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import useAuth from "../../hooks/useAuth";
import StudentsTable from "../../components/Tutor/StudentsTable";
import { getTutorStudents } from "../../utils/tutor/getTutorStudents";
import { getDiagnosticEvents } from "../../utils/tutor/getDiagnosticEvents";
import { filterTestStudentsForTutor } from "../../utils/tutor/filterTestStudents";

const TutorStudents = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (!user?.uid) return;

        const rawStudents = await getTutorStudents();
        const baseStudents = filterTestStudentsForTutor({
          tutorUid: user?.uid,
          students: rawStudents,
        });

        const enriched = await Promise.all(
          baseStudents.map(async (student) => {
            const diagnosticEvents = await getDiagnosticEvents({
              userId: student.id,
              firstName: student.firstName,
            });

            const mostRecentDiagnostic =
              diagnosticEvents.sort((a, b) => b.sortTime - a.sortTime)[0] ?? null;

            const candidates = [];

            // Assignment activity (name only)
            if (student.lastActive && student.recentAssignment) {
              candidates.push({
                time: student.lastActive,
                name: student.recentAssignment, // <-- name only
                score: student.recentScore ?? null,
              });
            }

            // Diagnostic activity (name only)
            if (mostRecentDiagnostic) {
              const diagnosticName = String(mostRecentDiagnostic.action || "")
                .replace(/^completed\s+/i, "") // "SAT Math Diagnostic"
                .trim();

              candidates.push({
                time: mostRecentDiagnostic.sortTime,
                name: diagnosticName,
                score: mostRecentDiagnostic.score ?? null,
              });
            }

            // Signup (counts for lastActive, but no "Recent Activity" name)
            if (student.createdAt?.toDate) {
              candidates.push({
                time: student.createdAt.toDate(),
                name: null, // show "—" in Recent Activity column
                score: null,
              });
            }

            const mostRecent =
              candidates.sort((a, b) => b.time - a.time)[0] ?? null;

            return {
              ...student,

              // used by Last Active column
              lastActive: mostRecent?.time ?? student.lastActive ?? null,

              // used by Recent Activity column (THIS is what the table renders)
              recentAssignment: mostRecent?.name ?? null,

              // used by Score column
              recentScore: mostRecent?.score ?? null,
            };
          })
        );

        setStudents(enriched);
      } catch (err) {
        console.error("Failed to load tutor students", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.uid]);

  return (
    <>
      <Typography variant="h4" fontWeight={600} mb={1}>
        Students
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" mb={3}>
        Track student activity and performance
      </Typography>

      <StudentsTable students={students} loading={loading} />
    </>
  );
};

export default TutorStudents;
